import { useMemo, useState } from 'react';
import { Button } from '../components/Button';
import { Field } from '../components/Field';
import { Modal } from '../components/Modal';
import { Table } from '../components/Table';

/**
 * CSV import — paste, preview, commit, as the three API calls the EJS dialog makes.
 *
 *   POST /lists/:id/import/preview  → headers, sample, colMap, rawTypes, rawTickers, looksLikePositions
 *   POST /lists/:id/import/commit   → imported / skipped
 *
 * and the SAME three gates the EJS dialog enforces, because each of them is a way to import the wrong data
 * confidently:
 *
 *   1. **Unmapped types** — a Type column whose values the importer does not recognise ("Бuy", "PURCHASE").
 *      Nothing commits until every raw value has a target type.
 *   2. **Unknown tickers** — symbols no price provider knows. They are usually typos, so the import waits
 *      for an explicit acknowledgement.
 *   3. **A positions snapshot** — a file that is a holdings list, not a transaction log. Committing one
 *      produces a single buy per position at today's price and a fake cost basis; it needs a deliberate yes.
 */
type Preview = {
  headers: string[];
  sample: string[][];
  totalRows: number;
  colMap: Record<string, number>;
  autoMap: boolean;
  valueMaps: { TYPE?: Record<string, string> };
  suggestedTypes: Record<string, string>;
  rawTypes: string[];
  rawTickers: Record<string, { known: boolean; name?: string }>;
  remembered?: { colMap?: Record<string, number> } | null;
  looksLikePositions: boolean;
};

const TYPES = ['buy', 'sell', 'dividend', 'drip', 'split', 'transfer_in', 'transfer_out', 'fee', 'deposit', 'withdrawal'];

export function ImportDialog({ listId, open, onClose, onImported }: { listId: number; open: boolean; onClose: () => void; onImported: () => void }) {
  const [text, setText] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [typeMap, setTypeMap] = useState<Record<string, string>>({});
  const [acceptUnknown, setAcceptUnknown] = useState(false);
  const [tickerMap, setTickerMap] = useState<Record<string, string>>({});
  // The mapping panel's state, seeded from the preview's suggestion (or from what this user chose for these
  // headers last time — the service remembers it per header signature). -1 means "— none —".
  const [colMap, setColMap] = useState<Record<string, number>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ imported: number; skipped: number } | null>(null);

  const unknown = useMemo(
    () => Object.entries(preview?.rawTickers ?? {}).filter(([, v]) => !v.known).map(([t]) => t),
    [preview],
  );
  const unmapped = useMemo(
    () => (preview?.rawTypes ?? []).filter((t) => !(typeMap[t] ?? preview?.suggestedTypes?.[t.toLowerCase()])),
    [preview, typeMap],
  );

  const reset = () => {
    setText('');
    setPreview(null);
    setTypeMap({});
    setAcceptUnknown(false);
    setError(null);
    setResult(null);
  };

  const post = async (path: string, body: unknown) => {
    const res = await fetch(`/api/v1/lists/${listId}/import/${path}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => null);
    if (!res.ok || !json?.ok) throw new Error(json?.error || `HTTP ${res.status}`);
    return json.data;
  };

  const doPreview = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const p: Preview = await post('preview', { text });
      // What this user chose for THESE headers last time wins over the suggestion: the service returns the
      // remembered map beside the guess, and a second upload should arrive already mapped. Applied HERE, in the
      // same update as the preview: as a `useEffect` on [preview] it landed one render later, so the summary was
      // already on screen while the mapping card still showed Date unchosen — a reader (and the browser test
      // waiting on the summary) could act on a mapping the page was about to change.
      setColMap({ ...p.colMap, ...(p.remembered?.colMap || {}) });
      setTickerMap({});
      setPreview(p);
      // Prefill from the importer's own suggestions — the gate is for the values it could NOT suggest.
      const seeded: Record<string, string> = {};
      for (const raw of p.rawTypes) {
        const s = p.suggestedTypes?.[raw.toLowerCase()];
        if (s) seeded[raw] = s;
      }
      setTypeMap(seeded);
    } catch (e) {
      setError((e as Error).message);
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  const doCommit = async () => {
    if (!preview) return;
    setBusy(true);
    setError(null);
    try {
      const r = await post('commit', {
        text,
        colMap,
        // The service lower-cases raw ticker keys when it remembers them; send them the same way so a chosen
        // symbol is what the next upload of this file shape starts with.
        valueMaps: {
          TYPE: typeMap,
          TICKER: Object.fromEntries(Object.entries(tickerMap).filter(([, v]) => v).map(([k, v]) => [k.toLowerCase(), v])),
        },
        defaultDate: new Date().toISOString().slice(0, 10),
      });
      setResult(r);
      onImported();
      // The EJS page reloads after a commit, and so does this: the list behind the dialog is stale otherwise,
      // and a test (or a reader) that waits for the load event is waiting for the rows to exist.
      window.location.reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // The spec's required set: date, ticker, type, quantity, price. Import stays dimmed until each has a column.
  // The spec's required set: date, ticker, type, quantity, price. Import stays dimmed until each has a column.
  const REQUIRED = ['DATE', 'TICKER', 'TYPE', 'QUANTITY', 'PRICE'];
  const missingCols = preview
    ? ['DATE', 'TICKER', 'TYPE', 'QUANTITY', 'PRICE'].filter((f) => (colMap[f] ?? -1) < 0)
    : [];
  const blocked =
    !preview ||
    missingCols.length > 0 ||
    unmapped.length > 0 ||
    unknown.some((raw) => !(tickerMap[raw] ?? '').trim()) ||
    preview.looksLikePositions;

  return (
    <Modal
      id="import-csv-modal"
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="Import a CSV"
      size="lg"
      subtitle="Paste the file, check the mapping, then import."
    >
      {error ? (
        <div className="error" id="import-error" style={{ marginBottom: 12 }}>
          {error}
        </div>
      ) : null}

      {result ? (
        <>
          <p className="t-body">
            Imported <strong>{result.imported}</strong> transactions{result.skipped ? `, skipped ${result.skipped}` : ''}.
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              onClick={() => {
                reset();
                onClose();
              }}
            >
              Done
            </Button>
          </div>
        </>
      ) : (
        <>
          <Field label="CSV" htmlFor="import-paste" hint="Header row required. Nothing is written until you import.">
            <textarea
              id="import-paste"
              rows={7}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                // A preview describes the text it was built from. Editing the CSV leaves the gates and the
                // row count describing a file that is no longer on screen — clear it and make the user
                // preview again.
                if (preview) setPreview(null);
              }}
              placeholder={'Date,Symbol,Quantity,Price,Action\n2026-01-15,AAPL,10,150,Buy'}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
          </Field>

          {!preview ? (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
              <Button id="import-paste-preview" onClick={doPreview} disabled={busy || !text.trim()}>
                {busy ? 'Reading…' : 'Preview'}
              </Button>
            </div>
          ) : (
            <>
              <p className="muted t-small" id="import-summary" style={{ margin: '12px 0 8px' }}>
                Showing {Math.min(5, preview.sample.length)} of {preview.totalRows} rows
              </p>
              {/* Only a complaint is worth a line of its own, and it has to NAME the values it cannot read: a
                  message that says "check the mapping" makes the reader hunt for which cell is wrong. */}
              {unmapped.length || unknown.length ? (
                <p className="t-small" id="import-map-status" style={{ margin: '0 0 8px', color: 'var(--muted)' }}>
                  {unmapped.length ? `Type values with no meaning yet: ${unmapped.join(', ')}.` : ''}
                  {unmapped.length && unknown.length ? ' ' : ''}
                  {unknown.length ? `Tickers no price provider knows: ${unknown.join(', ')}.` : ''}
                </p>
              ) : null}

              {/* The mapping panel: one control per field, with "— none —" first. Date and Type are required;
                  the service remembers whatever this user chose for these headers, so a second upload of the
                  same shape arrives already mapped. */}
              <div id="import-map" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, margin: '0 0 12px' }}>
                {['DATE', 'TYPE', 'TICKER', 'QUANTITY', 'PRICE', 'AMOUNT', 'FEES', 'NOTES'].map((f) => {
                  const v = colMap[f] ?? -1;
                  const bad = REQUIRED.includes(f) && v < 0;
                  return (
                    <label key={f} className="field" style={bad ? { color: 'var(--red)' } : undefined}>
                      <span className="label">
                        {f}
                        {REQUIRED.includes(f) ? ' *' : ''}
                      </span>
                      <select
                        data-field={f}
                        value={String(v)}
                        onChange={(e) => setColMap((m) => ({ ...m, [f]: Number(e.target.value) }))}
                      >
                        <option value="-1">— none —</option>
                        {preview.headers.map((h, i) => (
                          <option key={h + i} value={String(i)}>
                            {h}
                          </option>
                        ))}
                      </select>
                    </label>
                  );
                })}
              </div>

              <Table
                tableId="import-preview"
                expandable={false}
                compact
                columns={preview.headers.map((h) => ({ label: h }))}
                rows={preview.sample.slice(0, 5).map((r, i) => ({
                  key: i,
                  cells: r.map((cell, j) => {
                    const bad =
                      (unmapped.includes(String(cell)) && preview.headers[j]?.toUpperCase().includes('TYPE')) ||
                      unknown.includes(String(cell).toUpperCase());
                    // The colour goes on the CELL: the page's own check reads the td's computed colour, and a
                    // span inside it leaves the cell black.
                    return bad ? { key: j, content: cell, className: 'neg' } : cell;
                  }),
                }))}
              />

              {unmapped.length ? (
                <div className="panel-card" style={{ marginTop: 12, padding: 12 }}>
                  <p className="t-body" style={{ margin: '0 0 8px' }}>
                    <strong>Gate 1 — unmapped types.</strong> These values need a transaction type:
                  </p>
                  {unmapped.map((raw) => (
                    <Field key={raw} label={raw} htmlFor={`type-${raw}`} style={{ marginBottom: 6 }}>
                      <select id={`type-${raw}`} value={typeMap[raw] ?? ''} onChange={(e) => setTypeMap((m) => ({ ...m, [raw]: e.target.value }))}>
                        <option value="">— pick one —</option>
                        {TYPES.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </Field>
                  ))}
                </div>
              ) : null}

              {unknown.length ? (
                <div id="import-ticker-maps" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
                  {unknown.map((raw) => (
                    <label key={raw} className="field" style={{ minWidth: 140 }}>
                      <span className="label" style={{ color: 'var(--red)' }}>
                        {raw} ⚠
                      </span>
                      <input
                        data-rawticker={raw}
                        value={tickerMap[raw] ?? ''}
                        placeholder="map to a symbol"
                        onChange={(e) => setTickerMap((m) => ({ ...m, [raw]: e.target.value.toUpperCase() }))}
                      />
                    </label>
                  ))}
                </div>
              ) : null}

              {unknown.length ? (
                <div className="panel-card" style={{ marginTop: 12, padding: 12 }}>
                  <p className="t-body" style={{ margin: '0 0 6px' }}>
                    <strong>Gate 2 — tickers no price provider knows:</strong> {unknown.join(', ')}
                  </p>
                  <label className="t-body" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input type="checkbox" checked={acceptUnknown} onChange={(e) => setAcceptUnknown(e.target.checked)} />
                    They are real symbols; import them anyway (they will show as unpriced)
                  </label>
                </div>
              ) : null}

              {preview.looksLikePositions ? (
                <div className="error" id="import-error" style={{ marginTop: 12 }}>
                  This looks like a <strong>positions snapshot</strong>, not a transaction log — the Type column
                  holds account types rather than trade actions. Importing it would create one buy per position
                  at today's price and a made-up cost basis, so it is refused.
                </div>
              ) : null}

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
                <Button variant="secondary" id="import-cancel" onClick={() => { reset(); onClose(); }}>Cancel</Button>
                <Button variant="secondary" onClick={() => setPreview(null)}>
                  Back
                </Button>
                <Button id="import-confirm" onClick={doCommit} disabled={blocked || busy}>
                  {busy ? 'Importing…' : `Import ${preview.totalRows} rows`}
                </Button>
              </div>
            </>
          )}
        </>
      )}
    </Modal>
  );
}
